/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import { useState, useMemo } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import TableFooter from '@mui/material/TableFooter';
import TableSortLabel from '@mui/material/TableSortLabel';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import { visuallyHidden } from '@mui/utils';

import { formatDate } from '../sharedcomponents/utils';
import { getCMECertificateThunk, selectCMEState } from './cmeSlice';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import styles from './CMETable.module.scss';
import { CME_DATE_FORMAT } from '../sharedcomponents/constants';
import { CMEDataType } from './cmeTypes';
import { useStateLocalStorage } from '../sharedcomponents/useHooks';

interface HeadCell {
  id: keyof CMEDataType;
  label: string;
  align: 'left' | 'right';
  sort?: boolean;
}

const headCells: readonly HeadCell[] = [
  {
    id: 'created_at',
    label: 'Certificate Date',
    align: 'left',
    sort: true,
  },
  {
    id: 'total_credits',
    label: 'Credits',
    align: 'right',
    sort: true,
  },
];

function ClaimedTableHead(props: {
  order: 'asc' | 'desc';
  orderBy: string;
  handleSort: (sortBy: keyof CMEDataType) => void;
}) {
  const { order, orderBy, handleSort } = props;

  return (
    <TableHead>
      <TableRow>
        {headCells.map((cell) => (
          <TableCell
            key={cell.id}
            className={styles.tableHeader}
            align={cell.align}
            sortDirection={orderBy === cell.id ? order : false}
          >
            {cell.sort ? (
              <TableSortLabel
                active={orderBy === cell.id}
                direction={orderBy === cell.id ? order : 'asc'}
                onClick={() => handleSort(cell.id)}
              >
                {cell.label}
                {orderBy === cell.id ? (
                  <Box component="span" sx={visuallyHidden}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            ) : (
              cell.label
            )}
          </TableCell>
        ))}
        <TableCell />
      </TableRow>
    </TableHead>
  );
}

export default function CMECertificates() {
  const { cmeList: rows, fetchingCMEList } = useAppSelector(selectCMEState);
  const dispatch = useAppDispatch();
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [orderBy, setOrderBy] = useState<keyof CMEDataType>('created_at');
  const [rowsPerPage, setRowsPerPage] = useStateLocalStorage('cme.pageSize', 25);

  const handleSort = (sortBy: keyof CMEDataType) => {
    if (sortBy !== orderBy) {
      setOrderBy(sortBy);
      setOrder('asc');
    } else {
      setOrder(order === 'asc' ? 'desc' : 'asc');
    }
  };
  const downloadCertificate = (url: string = '') => dispatch(getCMECertificateThunk({ url, download: true }));

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  const visibleRows = useMemo(() => {
    const sortedList = [...rows]?.sort(
      (a: CMEDataType, b: CMEDataType) => ((a[orderBy] || 0) > (b[orderBy] || 0) ? 1 : -1) * (order === 'asc' ? 1 : -1)
    );
    return sortedList.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [page, rowsPerPage, order, orderBy, rows]);

  return (
    <Box sx={{ width: '100%', pl: 1, pr: 1 }}>
      <TableContainer className={styles.certificateTableContainer}>
        <Table stickyHeader size="small" aria-labelledby="bookmark-table">
          <ClaimedTableHead order={order} orderBy={orderBy} handleSort={handleSort} />
          <TableBody>
            {visibleRows.map((row) => {
              return (
                <TableRow tabIndex={-1} key={row.id}>
                  <TableCell className={styles.cells} align="left" id={row.certificate_date}>
                    {formatDate(row.created_at, CME_DATE_FORMAT)}
                  </TableCell>
                  <TableCell className={styles.cells} align="right">
                    {row.total_credits?.toFixed(2)}
                  </TableCell>
                  <TableCell className={styles.cells} align="right" id={row.pdf}>
                    <Button color="green" variant="contained" onClick={() => downloadCertificate(row.pdf)}>
                      Download
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
            {emptyRows > 0 && (
              <TableRow
                style={{
                  height: 53 * emptyRows,
                }}
              >
                <TableCell colSpan={3} />
              </TableRow>
            )}
            {fetchingCMEList && (
              <TableRow>
                <TableCell align="center" colSpan={3}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
          {!rows.length && !fetchingCMEList && (
            <TableFooter>
              <TableRow>
                <TableCell className={styles.tableHeader} colSpan={3} align="center">
                  No CME certificates available
                </TableCell>
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[8, 15, 25]}
        component="div"
        count={rows.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        className={styles.pagination}
      />
    </Box>
  );
}
